/**
 * useDownloadQuoteGate — ask the backend what a set of model downloads costs
 * (``POST download/quote``) before requesting them, and own the two billing
 * modals that decision can open:
 *
 * - nothing to pay (``total_cents === 0`` and not blocked) → approve at once;
 * - blocked (``free_usage_exhausted`` / ``insufficient_credits``) and the
 *   workspace is enforced → ``InsufficientCreditsModal``;
 * - otherwise → ``PaidDownloadConfirmModal`` listing each version's price.
 *
 * Pair with ``DownloadQuoteModals`` (``../components/DownloadQuoteModals``),
 * which renders both modals from the state returned here. Callers that fire
 * the approved download must pass ``suppressFreeUsageGate: true`` so a 402
 * from ``download-model-request`` never also opens the global
 * FreeUsageExhausted modal — ``showInsufficientFromLastQuote`` is the single
 * place a payment refusal is surfaced.
 */
import { useCallback, useRef, useState } from 'react';
import { sparkToast } from '@spark';

import { DOWNLOAD_QUOTE_ERROR, fetchDownloadQuote } from '../api/billingApi';

export const quoteItemKey = (item) => `${item.llm_usecase_id}:${item.version}`;

/** Keep the first occurrence of each ``llm_usecase_id + version`` pair. */
export function dedupeQuoteItems(items) {
    const seen = new Set();
    return (items || []).filter((item) => {
        const key = quoteItemKey(item);
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
    });
}

/** ``'approve'`` | ``'insufficient'`` | ``'confirm'`` for a ``download/quote`` response. */
export function decideDownloadQuote(quote) {
    const total = Number(quote?.total_cents) || 0;
    if (total === 0 && !quote?.blocked_reason) return 'approve';
    if (quote?.blocked_reason && !quote?.enforcement_exempt) return 'insufficient';
    return 'confirm';
}

const insufficientStateFrom = (quote) => ({
    totalCents: Number(quote?.total_cents) || 0,
    balanceCents: Number(quote?.balance_cents) || 0,
});

export function useDownloadQuoteGate() {
    // { quote, items, onApproved } while the paid-download confirm is open.
    const [confirmState, setConfirmState] = useState(null);
    // { totalCents, balanceCents } while "Not enough credits" is open.
    const [insufficientState, setInsufficientState] = useState(null);
    const lastQuoteRef = useRef(null);

    /**
     * Quote ``items`` (``[{llm_usecase_id, version, label}]``) and either run
     * ``onApproved`` straight away or open the matching modal. Resolves to
     * ``true`` when the download was approved immediately. ``confirmInline``
     * skips the paid-download confirm: the caller already showed the price
     * (the Configure deployment modal's footer) and the click is the consent.
     */
    const requestDownload = useCallback(async ({ items, onApproved, confirmInline = false }) => {
        const deduped = dedupeQuoteItems(items);
        if (!deduped.length) return false;
        let quote;
        try {
            quote = await fetchDownloadQuote(
                deduped.map(({ llm_usecase_id, version }) => ({ llm_usecase_id, version }))
            );
        } catch (_err) {
            sparkToast.error(DOWNLOAD_QUOTE_ERROR);
            return false;
        }
        lastQuoteRef.current = quote;
        const decision = decideDownloadQuote(quote);
        if (decision === 'approve' || (decision === 'confirm' && confirmInline)) {
            await onApproved?.();
            return true;
        }
        if (decision === 'insufficient') {
            setInsufficientState(insufficientStateFrom(quote));
            return false;
        }
        setConfirmState({ quote, items: deduped, onApproved });
        return false;
    }, []);

    const handleConfirm = useCallback(() => {
        const approved = confirmState?.onApproved;
        setConfirmState(null);
        approved?.();
    }, [confirmState]);

    const handleCancel = useCallback(() => setConfirmState(null), []);

    /** Safety net for a 402 from the download request itself. */
    const showInsufficientFromLastQuote = useCallback(() => {
        setInsufficientState(insufficientStateFrom(lastQuoteRef.current));
    }, []);

    const closeInsufficient = useCallback(() => setInsufficientState(null), []);

    return {
        requestDownload,
        confirmState,
        insufficientState,
        handleConfirm,
        handleCancel,
        showInsufficientFromLastQuote,
        closeInsufficient,
    };
}

export default useDownloadQuoteGate;
